import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { Reporter, Comment, ApiResponse, PaginatedResponse, SimilarReporter } from '@pf/shared';
import { apiGet, apiPost, apiDelete } from '../lib/api';

export interface ReportersParams {
  page?: number;
  limit?: number;
  q?: string;
  publicationId?: string;
  beat?: string;
  hasEmail?: boolean;
  location?: string;
  articleFrequency?: 'daily' | 'weekly' | 'monthly';
  enrichmentStatus?: string;
}

export interface ReportersSearchParams {
  q: string;
  page?: number;
  limit?: number;
  publicationId?: string;
  beat?: string;
  hasEmail?: boolean;
  location?: string;
  articleFrequency?: 'daily' | 'weekly' | 'monthly';
}

const REPORTERS_KEY = 'reporters';
const COMMENTS_KEY = 'comments';

export function useReporters(params: ReportersParams = {}) {
  return useQuery({
    queryKey: [REPORTERS_KEY, params],
    queryFn: () =>
      apiGet<PaginatedResponse<Reporter>>('/reporters', {
        page: params.page ?? 1,
        limit: params.limit ?? 25,
        q: params.q,
        publicationId: params.publicationId,
        beat: params.beat,
        hasEmail: params.hasEmail,
        enrichmentStatus: params.enrichmentStatus,
      }),
  });
}

export function useReportersInfinite(params: Omit<ReportersParams, 'page'> = {}) {
  return useInfiniteQuery({
    queryKey: [REPORTERS_KEY, 'infinite', params],
    queryFn: ({ pageParam }: { pageParam: number }) =>
      apiGet<PaginatedResponse<Reporter>>('/reporters', {
        page: pageParam,
        limit: params.limit ?? 25,
        q: params.q,
        publicationId: params.publicationId,
        beat: params.beat,
        hasEmail: params.hasEmail,
        location: params.location,
        articleFrequency: params.articleFrequency,
        enrichmentStatus: params.enrichmentStatus,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const totalLoaded = allPages.reduce((sum, p) => sum + p.data.length, 0);
      return totalLoaded < lastPage.total ? allPages.length + 1 : undefined;
    },
  });
}

export function useReportersSearchInfinite(params: ReportersSearchParams & { enabled?: boolean }) {
  return useInfiniteQuery({
    queryKey: [REPORTERS_KEY, 'search', 'infinite', params],
    queryFn: ({ pageParam }: { pageParam: number }) =>
      apiGet<PaginatedResponse<Reporter> & { searchMode: string }>('/reporters/search', {
        page: pageParam,
        limit: params.limit ?? 25,
        q: params.q,
        publicationId: params.publicationId,
        beat: params.beat,
        hasEmail: params.hasEmail,
        location: params.location,
        articleFrequency: params.articleFrequency,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const totalLoaded = allPages.reduce((sum, p) => sum + p.data.length, 0);
      return totalLoaded < lastPage.total ? allPages.length + 1 : undefined;
    },
    enabled: params.enabled !== undefined ? params.enabled : !!params.q,
  });
}

export function useReporterBeats() {
  return useQuery({
    queryKey: [REPORTERS_KEY, 'beats'],
    queryFn: () => apiGet<{ beats: string[] }>('/reporters/beats'),
    staleTime: 1000 * 60 * 10,
  });
}

export function useReporter(id: string | undefined) {
  return useQuery({
    queryKey: [REPORTERS_KEY, id],
    queryFn: () => apiGet<ApiResponse<Reporter>>(`/reporters/${id}`),
    enabled: !!id,
  });
}

export function useReporterSimilar(reporterId: string | undefined) {
  return useQuery({
    queryKey: [REPORTERS_KEY, reporterId, 'similar'],
    queryFn: () =>
      apiGet<{ data: SimilarReporter[] }>(`/reporters/${reporterId}/similar`),
    enabled: !!reporterId,
    staleTime: 1000 * 60 * 10,
  });
}

// ── Comments ──────────────────────────────────────────────────────────────────

export function useReporterComments(reporterId: string | undefined) {
  return useQuery({
    queryKey: [COMMENTS_KEY, 'reporter', reporterId],
    queryFn: () =>
      apiGet<{ comments: Comment[] }>(`/reporters/${reporterId}/comments`),
    enabled: !!reporterId,
  });
}

export function useAddReporterComment(reporterId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (text: string) =>
      apiPost<{ comment: Comment }>(`/reporters/${reporterId}/comments`, {
        text,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [COMMENTS_KEY, 'reporter', reporterId],
      });
    },
  });
}

export function useDeleteReporterComment(reporterId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (commentId: string) =>
      apiDelete(`/reporters/${reporterId}/comments/${commentId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [COMMENTS_KEY, 'reporter', reporterId],
      });
    },
  });
}
